/**
 * React's view of the connection.
 *
 * The protocol lives in `@gok/shared` and knows nothing about React; this is the thin
 * layer that turns its callbacks into state. Keeping the split means the part that broke
 * in 2023 — reconnect without resubscribe — is tested under Deno rather than being
 * something you can only discover by unplugging your laptop.
 */

import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type ClientMessage,
  type ConnectionStatus,
  ProtocolClient,
  type Transport,
} from '@gok/shared';

import { loadIdentity } from './identity.ts';

export interface Connection {
  readonly client: ProtocolClient | null;
  readonly status: ConnectionStatus;
  readonly userId: string | undefined;
  readonly username: string | undefined;
  /** Server time minus local time. Clocks must render against this, not `Date.now()`. */
  readonly clockOffset: number;
  readonly lastError: { code: string; message: string } | undefined;
  send(message: ClientMessage): void;
  /** Drop the last error. Call it when starting an action, so a stale failure from a
   * previous attempt is not still on screen next to a successful one. */
  clearError(): void;
}

const ConnectionContext = createContext<Connection | null>(null);

/**
 * Where the WebSocket lives.
 *
 * Same origin as the page by default, which is what the Caddy deployment serves and what
 * `vite dev` proxies. `VITE_GOK_SERVER` overrides it for a split deployment — the client
 * on a static host, the server somewhere else — and is baked in at build time, so it is a
 * property of the build rather than something the running page can be talked into.
 *
 * Both failure modes here are silent by default: a page that cannot reach its server just
 * says "Reconnecting" forever, and there is nothing on screen to say the address was
 * wrong rather than the server being down. So both are shouted about in the console.
 */
const socketUrl = (): string => {
  const configured = import.meta.env.VITE_GOK_SERVER?.trim();
  let url: URL;

  try {
    url = new URL('/ws', configured || globalThis.location.href);
  } catch {
    console.error(
      `VITE_GOK_SERVER is not a URL: ${JSON.stringify(configured)}. ` +
        "Falling back to this page's origin.",
    );
    url = new URL('/ws', globalThis.location.href);
  }

  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';

  // A browser refuses a plaintext socket from a secure page, and refuses it quietly.
  if (globalThis.location.protocol === 'https:' && url.protocol === 'ws:') {
    console.error(
      `Refusing to be useful: this page is HTTPS but the server is ${url.origin}. ` +
        'The browser blocks a plaintext socket from a secure page — the server needs TLS.',
    );
  }

  return url.toString();
};

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<ProtocolClient | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [user, setUser] = useState<
    { userId: string; username: string } | undefined
  >();
  const [lastError, setLastError] = useState<Connection['lastError']>();
  const offsetRef = useRef(0);
  const [clockOffset, setClockOffset] = useState(0);

  useEffect(() => {
    let disposed = false;
    let active: ProtocolClient | undefined;

    void (async () => {
      const identity = await loadIdentity();
      if (disposed) return;

      active = new ProtocolClient({
        identity,
        connect: (handlers) => {
          const socket = new WebSocket(socketUrl());
          socket.addEventListener('open', () => handlers.onOpen());
          socket.addEventListener('message', (event) => {
            if (typeof event.data === 'string') handlers.onMessage(event.data);
          });
          socket.addEventListener('close', () => handlers.onClose());
          // An error is always followed by a close, so there is nothing extra to do.
          socket.addEventListener('error', () => {});
          const transport: Transport = {
            send: (data) => socket.readyState === WebSocket.OPEN && socket.send(data),
            close: () => socket.close(),
          };
          return transport;
        },
        onStatus: setStatus,
        onWelcome: setUser,
        onError: setLastError,
        onClockOffset: (offset) => {
          // Only re-render when it moves enough to matter; every pong reports one.
          if (Math.abs(offset - offsetRef.current) < 250) return;
          offsetRef.current = offset;
          setClockOffset(offset);
        },
      });

      active.open();
      setClient(active);
    })();

    return () => {
      disposed = true;
      active?.close();
    };
  }, []);

  const send = useCallback(
    (message: ClientMessage) => {
      client?.send(message);
    },
    [client],
  );

  const clearError = useCallback(() => setLastError(undefined), []);

  const value = useMemo<Connection>(
    () => ({
      client,
      status,
      userId: user?.userId,
      username: user?.username,
      clockOffset,
      lastError,
      send,
      clearError,
    }),
    [client, status, user, clockOffset, lastError, send, clearError],
  );

  return <ConnectionContext value={value}>{children}</ConnectionContext>;
}

export function useConnection(): Connection {
  const value = use(ConnectionContext);
  if (!value) {
    throw new Error('useConnection must be used inside a ConnectionProvider');
  }
  return value;
}

export interface ChannelView<S> {
  readonly state: S | undefined;
  /** Connected but not yet resynced, or disconnected with the last state still shown. */
  readonly stale: boolean;
}

/**
 * Watch a channel.
 *
 * `reduce` must be stable across renders — pass the reducers from `@gok/shared`
 * directly rather than an inline closure, or every render will resubscribe.
 */
export function useChannel<S, E>(
  channel: string | null,
  reduce: (state: S, event: E) => S,
): ChannelView<S> {
  const { client } = useConnection();
  const [view, setView] = useState<ChannelView<S>>({
    state: undefined,
    stale: true,
  });

  useEffect(() => {
    if (!client || !channel) {
      setView({ state: undefined, stale: true });
      return;
    }
    return client.subscribe<S, E>(
      channel,
      reduce,
      (state, stale) => setView({ state, stale }),
    );
  }, [client, channel, reduce]);

  return view;
}
